"use client";

import { useId } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { Media } from "@/components/frontend/ui/media";
import { Button, type ButtonVariant } from "@/components/frontend/ui/button";
import type { MediaSliderBlock } from "@/payload-types";

interface MediaSliderCarouselProps {
	slides: NonNullable<NonNullable<MediaSliderBlock["slides"]>["items"]>[number][];
	slidesAmount?: number;
	slidesAmountMobile?: number;
	autoWidth?: boolean;
	mediaWrap?: string;
	navbtn?: ButtonVariant;
}

const navButtonClass = cn(
	"absolute top-1/2 z-10 translate-y-[calc(-50%-20px)]",
	"[&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40",
);

const autoWidthSlideClass = cn(
	"h-64! w-fit! sm:h-72!",
	"[&_.blurhash]:h-full",
	"[&_.blurhash>*]:h-full [&_.blurhash>*]:w-auto [&_.blurhash>*]:max-w-none",
	"[&_video]:h-full [&_video]:w-auto",
);

export const MediaSliderCarousel: React.FC<MediaSliderCarouselProps> = ({
	slides,
	slidesAmount = 3,
	slidesAmountMobile = 1,
	autoWidth = true,
	mediaWrap,
	navbtn,
}) => {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
	const prevClass = `media-slider-prev-${uid}`;
	const nextClass = `media-slider-next-${uid}`;

	if (slides.length === 0) return null;

	return (
		<div className="relative w-full">
			<Swiper
				modules={[Navigation, Pagination]}
				className="overflow-hidden!"
				navigation={{ prevEl: `.${prevClass}`, nextEl: `.${nextClass}` }}
				pagination={{ clickable: true }}
				style={{ "--swiper-pagination-color": "var(--ms-color-primary)" } as React.CSSProperties}
				spaceBetween={16}
				slidesPerView={autoWidth ? "auto" : slidesAmountMobile}
				breakpoints={autoWidth ? undefined : { 1024: { slidesPerView: slidesAmount } }}
			>
				{slides.map((slide, index) => (
					<SwiperSlide key={slide.id ?? index} className={autoWidth ? autoWidthSlideClass : undefined}>
						{slide.media && <Media media={{ ...slide.media, wrap: autoWidth ? null : mediaWrap }} />}
					</SwiperSlide>
				))}
			</Swiper>

			<Button variant={navbtn} type="button" aria-label="Previous slide" className={cn(prevClass, "left-3", navButtonClass)}>
				<ChevronLeft className="size-4" />
			</Button>
			<Button variant={navbtn} type="button" aria-label="Next slide" className={cn(nextClass, "right-3", navButtonClass)}>
				<ChevronRight className="size-4" />
			</Button>
		</div>
	);
};
